import { ApexOptions } from 'apexcharts';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';
import {
  displayAmount,
  MoneyValueCurrencyEnum,
} from '../../utils/CurrencyUtils';
import ControlledSelectInputV7 from '../controlled-inputs/ControlledSelectInputV7';
import ControlledTextInput from '../controlled-inputs/ControlledTextInput';
import TextFieldAdornment from '../TextFieldAdornment';

const DEFAULT_HOME_PRICE = 250000;

const DEFAULT_DOWN_PAYMENT = 50000;

const Chart = dynamic(() => import('react-apexcharts'), { ssr: false });

interface CostEstimationProps {
  listPrice: number;
  setEstimatedMonthlyPrice?(amount: number): void;
}

interface formData {
  homePrice: string;
  downPayment: string;
  interestRate: string;
  loan: string;
  propertyTax: string;
  homeownersInsurance: string;
  hoaFees: string;
}

const CostEstimation: React.FC<CostEstimationProps> = ({
  listPrice,
  setEstimatedMonthlyPrice,
}) => {
  const [showOptional, setShowOptional] = useState<boolean>(true);
  const { control, watch } = useForm<formData>({
    defaultValues: {
      homePrice: (listPrice || DEFAULT_HOME_PRICE).toString(),
      downPayment: (
        Math.floor(listPrice / 5) || DEFAULT_DOWN_PAYMENT
      ).toString(),
      interestRate: '5',
      loan: '30',
    },
  });
  const options: ApexOptions = {
    stroke: {
      curve: 'smooth',
      lineCap: 'round',
    },
    colors: ['#E6E6E6', '#A1E8FC', '#05C3F9'],
    plotOptions: {
      radialBar: {
        inverseOrder: true,
        startAngle: 0,
        endAngle: 360,
        offsetX: 0,
        offsetY: 0,
        hollow: {
          size: '40%',
          background: 'transparent',
        },
        track: {
          show: true,
          background: '#F3F4F6',
          strokeWidth: '100%',
          opacity: 1,
          margin: 3,
        },

        dataLabels: {
          show: false,
          name: {
            fontSize: '12px',
            fontWeight: '300',
          },
          value: {
            fontSize: '14px',
          },
        },
      },
    },
  };

  const parseAmountFromFormField = (key: keyof formData): number =>
    parseFloat(watch(key) || '0');

  const homePrice = parseAmountFromFormField('homePrice');
  const downPayment = parseAmountFromFormField('downPayment');
  const interestRate = parseAmountFromFormField('interestRate') / 100;
  const loan = parseAmountFromFormField('loan');
  const propertyTax = parseAmountFromFormField('propertyTax');
  const homeownersInsurance = parseAmountFromFormField('homeownersInsurance');
  const hoaFees = parseAmountFromFormField('hoaFees');

  const principalLoanAmount = homePrice - downPayment;
  const monthlyRate = interestRate / 12;
  const numberOfPayments = loan * 12;

  const monthlyPrice =
    principalLoanAmount *
      ((monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1)) || 0;

  const totalMonthlyPrice =
    monthlyPrice + propertyTax + homeownersInsurance + hoaFees;

  const downPaymentPercentage = ((downPayment / homePrice) * 100).toFixed(0);

  const getPercentageBarAmount = (amount: number): number =>
    (amount / totalMonthlyPrice) * 100 || 0;

  const series = [homeownersInsurance + hoaFees, propertyTax, monthlyPrice];

  useEffect(() => {
    if (setEstimatedMonthlyPrice) {
      setEstimatedMonthlyPrice(totalMonthlyPrice);
    }
  }, [totalMonthlyPrice]);

  return (
    <div className='grid lg:grid-cols-2 md:grid-cols-1 gap-3'>
      <div className='space-y-5'>
        <ControlledTextInput
          label='Home Price'
          name='homePrice'
          type='number'
          placeholder='Home Price'
          control={control}
          rules={{ required: 'Required' }}
          startAdornment={
            <TextFieldAdornment text='$' containerStyles='pr-0' />
          }
        />
        <ControlledTextInput
          label='Down Payment'
          name='downPayment'
          type='number'
          placeholder='Down Payment'
          control={control}
          rules={{ required: 'Required' }}
          startAdornment={
            <TextFieldAdornment text='$' containerStyles='pr-0' />
          }
          endAdornment={
            <TextFieldAdornment
              text={`${downPaymentPercentage.toString()}%`}
              containerStyles='border-l px-6'
            />
          }
        />
        <div className='grid grid-cols-2 gap-3'>
          <ControlledTextInput
            label='Interest Rate (%)'
            name='interestRate'
            type='number'
            placeholder='Interest Rate'
            control={control}
            rules={{ required: 'Required' }}
          />
          <ControlledSelectInputV7
            options={[
              { label: '5 Years', value: '5' },
              {
                label: '10 Years',
                value: '10',
              },
              { label: '15 Years', value: '15' },
              {
                label: '30 Years',
                value: '30',
              },
            ]}
            name='loan'
            control={control}
            label='Loan'
          />
        </div>
        <div className=''>
          <div
            className='flex items-center cursor-pointer'
            onClick={() => setShowOptional(!showOptional)}
          >
            <p className='font-inter text-lg mr-1'>Optional</p>
            <div className='mb-1.5'>
              {showOptional ? (
                <TbChevronDown size={16} />
              ) : (
                <TbChevronUp size={16} />
              )}
            </div>
          </div>
          {showOptional && (
            <div>
              <div className='mt-2'>
                <ControlledTextInput
                  label='Property Tax'
                  name='propertyTax'
                  type='number'
                  control={control}
                  placeholder='Property Tax'
                  startAdornment={
                    <TextFieldAdornment text='$' containerStyles='pr-0' />
                  }
                  endAdornment={
                    <TextFieldAdornment text='/mo' containerStyles='mr-2' />
                  }
                />
              </div>
              <div className='mt-2'>
                <ControlledTextInput
                  label='Homeowners insurance'
                  name='homeownersInsurance'
                  type='number'
                  control={control}
                  placeholder='Homeowners insurance'
                  startAdornment={
                    <TextFieldAdornment text='$' containerStyles='pr-0' />
                  }
                  endAdornment={
                    <TextFieldAdornment text='/mo' containerStyles='mr-2' />
                  }
                />
              </div>
              <div className='mt-2'>
                <ControlledTextInput
                  label='HOA Fees'
                  name='hoaFees'
                  type='number'
                  control={control}
                  placeholder='HOA Fees'
                  startAdornment={
                    <TextFieldAdornment text='$' containerStyles='pr-0' />
                  }
                  endAdornment={
                    <TextFieldAdornment text='/mo' containerStyles='mr-2' />
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className='flex flex-col items-center '>
        <div className='relative w-fit h-fit pl-2 '>
          <div className='absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center z-20'>
            <p className='font-primary-light text-xs'>TOTAL</p>
            <p className='font-inter text-sm'>
              {displayAmount(
                {
                  amount: totalMonthlyPrice,
                  currency: MoneyValueCurrencyEnum.Usd,
                },
                { hideCurrency: true }
              )}
            </p>
            <p className='font-primary-light text-xs text-slate-500'>/mo</p>
          </div>
          <Chart
            options={options}
            series={series.map(getPercentageBarAmount)}
            type='radialBar'
            width='300'
          />
        </div>
        <div className='m-2 p-2 bg-slate-50 rounded-lg w-full'>
          <div className='flex flex-row  justify-between w-full '>
            <div className='flex flex-row items-center whitespace-nowrap text-sm'>
              <div className='w-1.5 h-1.5 rounded-full bg-primary mx-2' />
              Principal & Interest
            </div>
            <div className='border-dotted border-b-2 border-black  w-full mb-2 ' />
            <div>
              {displayAmount(
                {
                  amount: monthlyPrice,
                  currency: MoneyValueCurrencyEnum.Usd,
                },
                { hideCurrency: true }
              )}
            </div>
          </div>

          <div className='flex flex-row justify-between w-full '>
            <div className='flex flex-row items-center whitespace-nowrap text-sm'>
              <div className='w-1.5 h-1.5 rounded-full bg-cyan-200 mx-2' />
              Property Taxes
            </div>
            <div className='border-dotted border-b-2 border-black w-full mb-2 ' />
            <div>
              {displayAmount(
                {
                  amount: propertyTax,
                  currency: MoneyValueCurrencyEnum.Usd,
                },
                { hideCurrency: true }
              )}
            </div>
          </div>
          <div className='flex flex-row justify-between w-full '>
            <div className='flex flex-row items-center whitespace-nowrap text-sm'>
              <div className='w-1.5 h-1.5 rounded-full bg-slate-200 mx-2' />
              Other
            </div>
            <div className='border-dotted border-b-2 border-black w-full mb-2 ' />
            <div>
              {displayAmount(
                {
                  amount: homeownersInsurance + hoaFees,
                  currency: MoneyValueCurrencyEnum.Usd,
                },
                { hideCurrency: true }
              )}
            </div>
          </div>
          <div className='ml-5'>
            <div className='flex flex-row justify-between w-full '>
              <div className='flex flex-row items-center whitespace-nowrap text-sm'>
                <div className='w-1.5 h-1.5 rounded-full bg-slate-200 mx-2' />
                Homeowners Insurance
              </div>
              <div className='border-dotted border-b-2 border-black w-full mb-2 ' />
              <div>
                {displayAmount(
                  {
                    amount: homeownersInsurance,
                    currency: MoneyValueCurrencyEnum.Usd,
                  },
                  { hideCurrency: true }
                )}
              </div>
            </div>
            <div className='flex flex-row justify-between w-full '>
              <div className='flex flex-row items-center whitespace-nowrap text-sm'>
                <div className='w-1.5 h-1.5 rounded-full bg-slate-200 mx-2' />
                HOA
              </div>
              <div className='border-dotted border-b-2 border-black w-full mb-2 ' />
              <div>
                {displayAmount(
                  {
                    amount: hoaFees,
                    currency: MoneyValueCurrencyEnum.Usd,
                  },
                  { hideCurrency: true }
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostEstimation;
